import { useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type {
  NotificationRule,
  NotificationRuleInput,
  ReferenceRecord,
  User,
} from '@/lib/types';
import {
  Button,
  Checkbox,
  Combobox,
  Field,
  Input,
  Modal,
  SegmentedControl,
  Select,
  type ComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { parseTargetId, targetId, type NotificationTarget } from './notification-targets';

/*
 * Add / edit one routing rule (`POST|PATCH /notifications/rules`).
 *
 * A rule names exactly ONE recipient: a staff user or an email address. An
 * address has no in-app inbox, so `channelInbox` is forced off for it.
 *
 * Edit sends only the keys that moved; an empty PATCH is a 422, so Save is
 * disabled until something changes.
 */

type RecipientKind = 'user' | 'email';

const FIELD_KEYS = [
  'targetKey',
  'targetType',
  'userId',
  'email',
  'scope',
  'regionId',
  'channelEmail',
  'channelInbox',
  'note',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];
type FieldErrors = Partial<Record<FieldKey, string>>;

interface Draft {
  target: string;
  kind: RecipientKind;
  userId: string;
  email: string;
  /** `all` or `region:<id>`. */
  scope: string;
  channelEmail: boolean;
  channelInbox: boolean;
  note: string;
  isActive: boolean;
}

const EMPTY_DRAFT: Draft = {
  target: '',
  kind: 'user',
  userId: '',
  email: '',
  scope: 'all',
  channelEmail: true,
  channelInbox: true,
  note: '',
  isActive: true,
};

function draftOf(rule: NotificationRule): Draft {
  return {
    target: targetId(rule.targetType, rule.targetKey),
    kind: rule.user ? 'user' : 'email',
    userId: rule.user ? String(rule.user.id) : '',
    email: rule.email ?? '',
    scope: rule.scope === 'region' && rule.region ? `region:${rule.region.id}` : 'all',
    channelEmail: rule.channelEmail,
    channelInbox: rule.user ? rule.channelInbox : false,
    note: rule.note ?? '',
    isActive: rule.isActive,
  };
}

/** The full rule this draft describes, or a reason it cannot be sent yet. */
function bodyOf(draft: Draft): NotificationRuleInput | string {
  const target = parseTargetId(draft.target);
  if (!target) return 'Choose what the rule applies to.';
  const email = draft.email.trim();
  const userId = Number(draft.userId);
  if (draft.kind === 'user' && !(userId > 0)) return 'Choose a recipient.';
  if (draft.kind === 'email' && !email) return 'Enter an email address.';
  if (!draft.channelEmail && !draft.channelInbox) return 'Choose at least one channel.';
  const regionId = draft.scope.startsWith('region:') ? Number(draft.scope.slice(7)) : null;
  const note = draft.note.trim();
  return {
    targetType: target.type,
    targetKey: target.key,
    userId: draft.kind === 'user' ? userId : null,
    email: draft.kind === 'email' ? email : null,
    scope: regionId ? 'region' : 'all',
    regionId,
    channelEmail: draft.channelEmail,
    channelInbox: draft.kind === 'user' ? draft.channelInbox : false,
    note: note || null,
    isActive: draft.isActive,
  };
}

function patchOf(rule: NotificationRule, body: NotificationRuleInput): Partial<NotificationRuleInput> {
  const patch: Partial<NotificationRuleInput> = {};
  if (body.targetType !== rule.targetType || body.targetKey !== rule.targetKey) {
    patch.targetType = body.targetType;
    patch.targetKey = body.targetKey;
  }
  const userId = rule.user?.id ?? null;
  if ((body.userId ?? null) !== userId || (body.email ?? null) !== (rule.email ?? null)) {
    patch.userId = body.userId ?? null;
    patch.email = body.email ?? null;
  }
  const regionId = rule.scope === 'region' ? (rule.region?.id ?? null) : null;
  if (body.scope !== rule.scope || (body.regionId ?? null) !== regionId) {
    patch.scope = body.scope;
    patch.regionId = body.regionId ?? null;
  }
  if (body.channelEmail !== rule.channelEmail) patch.channelEmail = body.channelEmail;
  if (body.channelInbox !== rule.channelInbox) patch.channelInbox = body.channelInbox;
  if ((body.note ?? null) !== (rule.note ?? null)) patch.note = body.note ?? null;
  if (body.isActive !== rule.isActive) patch.isActive = body.isActive;
  return patch;
}

function routeErrors(caught: unknown): { fields: FieldErrors; form: string | null } {
  if (!(caught instanceof ApiError)) {
    return { fields: {}, form: caught instanceof Error ? caught.message : 'The rule was not saved.' };
  }
  const routed = caught.routeFieldErrors(FIELD_KEYS);
  const fields: FieldErrors = { ...routed.fields };
  if (fields.targetType && !fields.targetKey) fields.targetKey = fields.targetType;
  if (fields.regionId && !fields.scope) fields.scope = fields.regionId;
  if (routed.unroutable.length > 0) return { fields, form: routed.unroutable.join(' ') };
  if (Object.keys(fields).length === 0) return { fields, form: caught.formMessage ?? caught.message };
  return { fields, form: null };
}

export interface NotificationRuleModalProps {
  open: boolean;
  /** `null` = add. */
  rule: NotificationRule | null;
  targets: NotificationTarget[];
  staff: { users: User[]; loading: boolean; failed: boolean; hasMore: boolean };
  regions: ReferenceRecord[];
  onClose: () => void;
  onSaved: () => void;
}

export function NotificationRuleModal(props: NotificationRuleModalProps) {
  if (!props.open) return null;
  // Remounted per open so the draft always starts from the rule being edited.
  return <RuleForm key={props.rule?.id ?? 'new'} {...props} />;
}

function RuleForm({ rule, targets, staff, regions, onClose, onSaved }: NotificationRuleModalProps) {
  const [draft, setDraft] = useState<Draft>(() => (rule ? draftOf(rule) : EMPTY_DRAFT));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const targetOptions = useMemo<ComboboxOption[]>(() => {
    const options = targets.map((target) => ({
      value: targetId(target.type, target.key),
      label: target.path,
    }));
    if (rule) {
      const current = targetId(rule.targetType, rule.targetKey);
      if (!options.some((option) => option.value === current)) {
        options.unshift({ value: current, label: rule.targetKey });
      }
    }
    return options;
  }, [targets, rule]);

  const userOptions = useMemo<ComboboxOption[]>(() => {
    const options = staff.users.map((user) => ({
      value: String(user.id),
      label: user.name.trim() || user.email,
      hint: user.email,
    }));
    if (rule?.user && !options.some((option) => option.value === String(rule.user?.id))) {
      options.unshift({
        value: String(rule.user.id),
        label: rule.user.name.trim() || rule.user.email,
        hint: rule.user.email,
      });
    }
    return options;
  }, [staff.users, rule]);

  const regionOptions = useMemo(() => {
    const options = regions.map((region) => ({ id: region.id, name: region.name }));
    if (rule?.region && !options.some((option) => option.id === rule.region?.id)) {
      options.push({ id: rule.region.id, name: rule.region.name });
    }
    return options;
  }, [regions, rule]);

  const body = bodyOf(draft);
  const patch = rule && typeof body !== 'string' ? patchOf(rule, body) : null;
  const unchanged = patch !== null && Object.keys(patch).length === 0;
  const blockReason = typeof body === 'string' ? body : null;

  function change(next: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...next }));
    setFormError(null);
  }

  function save() {
    if (saving || typeof body === 'string' || unchanged) return;
    setSaving(true);
    setErrors({});
    setFormError(null);
    const request = rule && patch ? api.updateNotificationRule(rule.id, patch) : api.createNotificationRule(body);
    request
      .then(() => onSaved())
      .catch((caught: unknown) => {
        const routed = routeErrors(caught);
        setErrors(routed.fields);
        setFormError(routed.form);
      })
      .finally(() => setSaving(false));
  }

  return (
    <Modal
      open
      onClose={() => {
        if (!saving) onClose();
      }}
      title={rule ? 'Edit routing rule' : 'Add routing rule'}
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <span title={blockReason ?? undefined}>
            <Button onClick={save} disabled={blockReason !== null || unchanged} loading={saving}>
              {rule ? 'Save' : 'Add rule'}
            </Button>
          </span>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && (
          <Notice tone="danger" role="alert" title="Couldn’t save the rule.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{formError}</span>
          </Notice>
        )}

        <Field label="Applies to" error={errors.targetKey} required>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              aria-describedby={describedBy}
              value={draft.target}
              onChange={(value) => change({ target: value })}
              options={targetOptions}
              searchPlaceholder="Search notifications…"
              disabled={saving}
              invalid={Boolean(errors.targetKey)}
            />
          )}
        </Field>

        <div className="flex flex-col gap-2">
          <SegmentedControl
            label="Recipient type"
            value={draft.kind}
            options={[
              { value: 'user', label: 'Staff user' },
              { value: 'email', label: 'Email address' },
            ]}
            onChange={(kind) =>
              change(kind === 'email' ? { kind, channelInbox: false } : { kind, channelInbox: true })
            }
            disabled={saving}
          />
          {draft.kind === 'user' ? (
            <Field label="Recipient" error={errors.userId} required>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={draft.userId}
                  onChange={(value) => change({ userId: value })}
                  options={userOptions}
                  searchPlaceholder="Search staff…"
                  loading={staff.loading}
                  disabled={saving}
                  invalid={Boolean(errors.userId)}
                  {...(staff.failed
                    ? { footnote: 'The staff list could not be read.' }
                    : staff.hasMore
                      ? { footnote: 'Showing the first page of staff.' }
                      : {})}
                />
              )}
            </Field>
          ) : (
            <Field label="Recipient email" error={errors.email} required>
              {(fieldProps) => (
                <Input
                  {...fieldProps}
                  type="email"
                  value={draft.email}
                  maxLength={255}
                  disabled={saving}
                  invalid={Boolean(errors.email)}
                  onChange={(event) => change({ email: event.target.value })}
                />
              )}
            </Field>
          )}
        </div>

        <Field label="Scope" error={errors.scope}>
          {(fieldProps) => (
            <Select
              {...fieldProps}
              value={draft.scope}
              disabled={saving}
              invalid={Boolean(errors.scope)}
              onChange={(event) => change({ scope: event.target.value })}
            >
              <option value="all">All clients</option>
              {regionOptions.map((region) => (
                <option key={region.id} value={`region:${region.id}`}>
                  {region.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1.5 p-0 text-xs font-medium text-fg-secondary">Channels</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox
              label="Email"
              checked={draft.channelEmail}
              disabled={saving}
              onChange={(event) => change({ channelEmail: event.target.checked })}
            />
            <span title={draft.kind === 'email' ? 'An email address has no in-app inbox.' : undefined}>
              <Checkbox
                label="In-app"
                checked={draft.kind === 'user' && draft.channelInbox}
                disabled={saving || draft.kind === 'email'}
                onChange={(event) => change({ channelInbox: event.target.checked })}
              />
            </span>
          </div>
          {(errors.channelEmail || errors.channelInbox) && (
            <p role="alert" className="m-0 text-xs text-danger">
              {errors.channelEmail ?? errors.channelInbox}
            </p>
          )}
        </fieldset>

        <Field label="Note" error={errors.note}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              value={draft.note}
              maxLength={255}
              disabled={saving}
              invalid={Boolean(errors.note)}
              onChange={(event) => change({ note: event.target.value })}
            />
          )}
        </Field>

        <Checkbox
          label="Active"
          checked={draft.isActive}
          disabled={saving}
          onChange={(event) => change({ isActive: event.target.checked })}
        />
      </div>
    </Modal>
  );
}
